import { postToNative } from '@/lib/nativeBridge';
import type { AnalyticsParams } from './types';

const IOS_EVENT_NAME = 'gaEvent';

const WEB_ONLY_EVENT_FIELDS = new Set([
  'additional_details_1',
  'customer_id',
  'event_action',
  'event_category',
  'event_label',
  'event_navigation_action',
  'event_navigation_browsing_method',
  'event_navigation_name',
  'ga4_event_name',
]);

export interface IosTrackingEventMessage {
  name: typeof IOS_EVENT_NAME;
  parameters: Record<string, string>;
}

export interface IosTrackingScreenMessage {
  name: string;
  parameters: Record<string, string>;
}

export type IosTrackingDispatchStatus = 'dispatched' | 'failed';

const normalizeRequiredValue = (value: string | undefined): string | undefined => {
  const normalized = value?.trim();
  return normalized || undefined;
};

const withoutWebOnlyFields = (params: AnalyticsParams): Record<string, string> =>
  Object.fromEntries(
    Object.entries(params).filter(
      (entry): entry is [string, string] =>
        entry[1] !== undefined && !WEB_ONLY_EVENT_FIELDS.has(entry[0])
    )
  );

/**
 * Converts the existing web event data into the confirmed iOS bridge contract.
 * The native host owns customer_id enrichment, so it is intentionally excluded.
 */
export const buildIosTrackingEvent = (
  params: AnalyticsParams
): IosTrackingEventMessage | undefined => {
  try {
    const category = normalizeRequiredValue(params.event_category);
    const action = normalizeRequiredValue(params.event_action);
    const label = normalizeRequiredValue(params.event_label);
    const sectionReference = normalizeRequiredValue(params.section_reference);
    const interaction = normalizeRequiredValue(params.dtfantasy_interaction);

    if (!category || !action || !label || !sectionReference || !interaction) return undefined;

    return {
      name: IOS_EVENT_NAME,
      parameters: {
        ...withoutWebOnlyFields(params),
        ua_event_name: IOS_EVENT_NAME,
        ua_event_category: category,
        ua_event_action: action,
        ua_event_label: label,
      },
    };
  } catch {
    return undefined;
  }
};

export const buildIosTrackingScreen = (
  screenName: string,
  params: AnalyticsParams
): IosTrackingScreenMessage | undefined => {
  try {
    const name = normalizeRequiredValue(screenName);
    if (!name) return undefined;

    return {
      name,
      parameters: withoutWebOnlyFields(params),
    };
  } catch {
    return undefined;
  }
};

/** Tracking must never interrupt the game interaction that triggered it. */
export const dispatchIosTrackingEvent = (params: AnalyticsParams): IosTrackingDispatchStatus => {
  try {
    const event = buildIosTrackingEvent(params);
    if (!event) return 'failed';

    postToNative('analytics.trackEvent', event);
    return 'dispatched';
  } catch {
    return 'failed';
  }
};

/** Screen tracking follows the same no-throw boundary as interaction tracking. */
export const dispatchIosTrackingScreen = (
  screenName: string,
  params: AnalyticsParams
): IosTrackingDispatchStatus => {
  try {
    const screen = buildIosTrackingScreen(screenName, params);
    if (!screen) return 'failed';

    postToNative('analytics.trackScreen', screen);
    return 'dispatched';
  } catch {
    return 'failed';
  }
};
